import { getMainFillJustification } from '@miles/domain/Dashboard/Application/FillJustification/fillJustifications';
import { match, P } from 'ts-pattern';
import { getAgeFromBirthDate, getRelativeAgeFromBirthDate } from '../../../Utils/Date';
import { Gender } from '../../../Utils/Gender';
import { z } from 'zod';
export class FormFunnelConfigConditionService {
    constructor() {
        this.shouldDisplayFormToAttendee = (allConditions, attendee) => {
            if (!Array.isArray(allConditions)) {
                return false;
            }
            // at least one of allConditions must be respected
            return allConditions.some((condition) => Array.isArray(condition)
                ? // a condition is a conjunction of sub-conditions, so all of them must be met
                    condition.every((subCondition) => this.getComparisonMethod(subCondition)(attendee))
                : false);
        };
        this.getComparisonMethod = (subCondition) => match(FormFunnelConfigSchema.parse(subCondition))
            .with({ keyName: 'age' }, this.compareAge)
            .with({ keyName: 'license' }, this.checkLicense)
            .with({ keyName: 'isCaptain' }, this.isCaptain)
            .with({ keyName: 'gender' }, this.checkGender)
            .with({ keyName: 'group' }, this.checkGroup)
            .exhaustive();
        this.isCaptain = () => (attendee) => attendee.isCaptain;
        this.compareAge = ({ operator, value, minValue, maxValue }) => ({ ageAtRaceStart, ageAtRaceYear }) => {
            if (!ageAtRaceStart)
                return false;
            return match(operator)
                .with(ComparisonOperator.EQUALS, () => ageAtRaceStart === value)
                .with(P.union(ComparisonOperator.LOWER_THAN, ComparisonOperator.LTE), () => ageAtRaceStart <= value)
                .with(P.union(ComparisonOperator.GREATER_THAN, ComparisonOperator.GTE), () => ageAtRaceStart >= value)
                .with(ComparisonOperator.BETWEEN, () => !!ageAtRaceYear &&
                ageAtRaceYear >= minValue &&
                ageAtRaceYear <= maxValue)
                .exhaustive();
        };
        this.checkLicense = (config) => (attendee) => attendee.isLicensed === config.value;
        this.checkGender = (config) => (attendee) => attendee.gender === config.value;
        this.checkGroup = ({ groupIds, include }) => (attendee) => attendee.groupId
            ? include
                ? groupIds.includes(attendee.groupId)
                : !groupIds.includes(attendee.groupId)
            : !include;
    }
}
export const liveFormFunnelConfigConditionService = new FormFunnelConfigConditionService();
export const fromGetAttendeeFromIdToAttendeeData = (attendee) => {
    var _a, _b, _c, _d, _e, _f;
    const isLicensed = match(attendee)
        .with({
        licenseNumber: P.when((licenseNumber) => licenseNumber &&
            (!licenseNumber.toUpperCase().startsWith('P') ||
                licenseNumber.length < 11))
    }, () => true)
        .with({ licenseNumber: P.nullish }, P.when(() => !!getMainFillJustification(attendee.fillJustifications)), () => false)
        .otherwise(() => undefined);
    return {
        ageAtRaceStart: attendee.birthDate
            ? getAgeFromBirthDate(attendee.birthDate.toISOString(), (_b = (_a = attendee.race) === null || _a === void 0 ? void 0 : _a.startDate) === null || _b === void 0 ? void 0 : _b.toISOString())
            : undefined,
        ageAtRaceYear: attendee.birthDate
            ? getRelativeAgeFromBirthDate(attendee.birthDate.toISOString(), (_d = (_c = attendee.race) === null || _c === void 0 ? void 0 : _c.startDate) === null || _d === void 0 ? void 0 : _d.toISOString())
            : undefined,
        gender: (_e = attendee.gender) !== null && _e !== void 0 ? _e : undefined,
        isCaptain: !!attendee.teamRegistration,
        isLicensed,
        groupId: (_f = attendee.groupId) !== null && _f !== void 0 ? _f : undefined
    };
};
export var ComparisonOperator;
(function (ComparisonOperator) {
    ComparisonOperator["EQUALS"] = "equals";
    /**
     *  @deprecated Use `LTE` instead
     */
    ComparisonOperator["LOWER_THAN"] = "lowerthan";
    /**
     * @deprecated Use `GTE` instead
     */
    ComparisonOperator["GREATER_THAN"] = "greaterthan";
    ComparisonOperator["LTE"] = "lte";
    ComparisonOperator["GTE"] = "gte";
    ComparisonOperator["BETWEEN"] = "between";
})(ComparisonOperator || (ComparisonOperator = {}));
export const comparisonOperatorWithoutDeprecated = Object.values(ComparisonOperator).filter((operator) => !['lowerthan', 'greaterthan'].includes(operator));
const FormFunnelConfigCaptainSchema = z.object({
    keyName: z.literal('isCaptain')
});
const FormFunnelConfigGenderSchema = z.object({
    keyName: z.literal('gender'),
    value: z.nativeEnum(Gender)
});
// Base schema for discriminatedUnion compatibility
const FormFunnelConfigAgeBaseSchema = z.object({
    keyName: z.literal('age'),
    operator: z.nativeEnum(ComparisonOperator),
    value: z
        .number()
        .nullish()
        .transform((val) => (val === null ? undefined : val)),
    minValue: z.number().optional(),
    maxValue: z.number().optional()
});
// Schema with conditional validation that matches Yup behavior
const FormFunnelConfigAgeSchema = FormFunnelConfigAgeBaseSchema.refine((data) => {
    if (data.operator === ComparisonOperator.BETWEEN) {
        // For BETWEEN: minValue and maxValue required, and minValue <= maxValue
        return (data.minValue !== undefined &&
            data.maxValue !== undefined &&
            data.minValue <= data.maxValue);
    }
    // For all other operators: value required
    return data.value !== undefined;
}, {
    message: 'Invalid age configuration: for BETWEEN operator minValue and maxValue are required (with minValue <= maxValue), for other operators value is required'
});
const FormFunnelConfigLicenseSchema = z.object({
    keyName: z.literal('license'),
    value: z.boolean()
});
const FormFunnelConfigGroupSchema = z.object({
    keyName: z.literal('group'),
    groupIds: z.array(z.number()).nonempty(),
    include: z.boolean().default(true)
});
export const FormFunnelConfigSchema = z.discriminatedUnion('keyName', [
    FormFunnelConfigCaptainSchema,
    FormFunnelConfigAgeBaseSchema, // Use base schema without refine
    FormFunnelConfigLicenseSchema,
    FormFunnelConfigGenderSchema,
    FormFunnelConfigGroupSchema
]);
export const FormFunnelConfigConditionKeyNames = FormFunnelConfigSchema.options.map(({ shape: { keyName: { value } } }) => value);
